const DEFAULT_STATUS_ENDPOINT = 'https://status.finlist.app/api/status';

const INDICATOR_MAP = {
  none: 'operational',
  operational: 'operational',
  minor: 'degraded',
  maintenance: 'maintenance',
  major: 'outage',
  critical: 'outage',
  degraded: 'degraded'
};

function createFallbackId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `status-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function getStatusEndpoint() {
  return import.meta.env.VITE_STATUS_ENDPOINT || DEFAULT_STATUS_ENDPOINT;
}

function normalizeStatusPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const indicatorKey = (payload.status?.indicator || payload.indicator || 'unknown').toLowerCase();
  const indicator = INDICATOR_MAP[indicatorKey] || 'unknown';
  const description = payload.status?.description || payload.description || 'Status unavailable';
  const incidents = Array.isArray(payload.incidents)
    ? payload.incidents.map((incident) => ({
      id: incident.id || incident.shortlink || incident.name || createFallbackId(),
      name: incident.name || incident.title || incident.message || 'Active incident',
      status: incident.status || 'investigating',
      shortlink: incident.shortlink || incident.url || null
    }))
    : [];

  const updatedAt = payload.page?.updated_at || payload.updated_at || new Date().toISOString();

  return {
    indicator,
    description,
    incidents,
    updatedAt
  };
}

export async function fetchStatusPulse({ signal } = {}) {
  const endpoint = getStatusEndpoint();
  
  // In development, if the status endpoint is the default and unavailable, return operational status
  if (import.meta.env.DEV && endpoint === DEFAULT_STATUS_ENDPOINT) {
    return {
      indicator: 'operational',
      description: 'All systems operational',
      incidents: [],
      updatedAt: new Date().toISOString()
    };
  }
  
  const response = await fetch(endpoint, { signal });
  if (!response.ok) {
    throw new Error('Unable to load platform status');
  }

  const payload = await response.json();
  const normalized = normalizeStatusPayload(payload);

  if (!normalized) {
    throw new Error('Received invalid platform status response');
  }

  return normalized;
}

export default {
  fetchStatusPulse,
  getStatusEndpoint
};
